import { AgGridReact } from "ag-grid-react";
import { useCallback, useRef } from "react";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import {
  ShowSuccessFetch,
  showError,
  showWarning,
} from "src/utils/toast/toastUtil";

import {
  deleteKSK2130_R01,
  getKSK2130_R01DetailList,
  getKSK2130_R01List,
  getKSK2130_R01LotList,
  postKSK2130_R01,
} from "./Api_KSK2130_R01";
import {
  KSK2130_R01DetailDto,
  KSK2130_R01HeaderDto,
} from "./Dto_KSK2130_R01";
import { useKSK2130_R01Store } from "./Store_KSK2130_R01";

interface ContentKSK2130R01Props {
  gridRef: React.RefObject<AgGridReact<KSK2130_R01HeaderDto> | null>;
  detailGridRef: React.RefObject<AgGridReact<KSK2130_R01DetailDto> | null>;
  setMessage: (message: string) => void;
}

export const Content_KSK2130_R01 = ({
                                        gridRef,
                                        detailGridRef,
                                        setMessage,
                                    }: ContentKSK2130R01Props) => {
    const {
        KSK2130_R01Filter,
        setKSK2130_R01Filter,
        setKSK2130_R01List,
        selectedKSK2130_R01,
        setSelectedKSK2130_R01,
        KSK2130_R01DetailList,
        setKSK2130_R01DetailList,
        selectedKSK2130_R01DetailList,
        initSelectedKSK2130_R01DetailList,
        removeKSK2130_R01DetailList,
        deletedKSK2130_R01DetailList,
        addDeletedKSK2130_R01DetailList,
        removeDeletedKSK2130_R01DetailList,
        originalKSK2130_R01DetailList,
        insertKSK2130_R01DetailList,
        setKSK2130_R01ModalList,
        setIsMaterialLotModalVisible,
        setScrollTop,
        setDetailScrollTop,
        initData,
    } = useKSK2130_R01Store();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const { workPlaceList } = useCommonCodeStore();
    const detailRequestIdRef = useRef(0);

    const hasPendingChanges =
        KSK2130_R01DetailList.some((item) => item.inserted) ||
        deletedKSK2130_R01DetailList.length > 0;

    /** 작업공정코드로 공정정보의 출고창고 코드를 조회 */
    const getTargetOutWarehouse = useCallback(
        (workplaceCd?: string) =>
            workPlaceList.find((wp) => wp.workplaceCd === workplaceCd)?.outWarehouse || "",
        [workPlaceList],
    );

    const fetchDetailList = useCallback(
        async (
            productOrder: KSK2130_R01HeaderDto,
            shouldToggleSelection = true,
        ) => {
            if (
                shouldToggleSelection &&
                selectedKSK2130_R01?.productOrderNo === productOrder.productOrderNo
            ) {
                if (hasPendingChanges) {
                    showWarning("변경 내용을 저장하거나 초기화한 후 선택을 해제해 주세요.");
                    return false;
                }
                detailRequestIdRef.current++;
                setDetailScrollTop(null);
                setSelectedKSK2130_R01(undefined);
                setKSK2130_R01DetailList([]);
                setKSK2130_R01Filter({ barcodeInput: "" });
                return false;
            }
            if (hasPendingChanges) {
                showWarning("변경 내용을 저장하거나 초기화한 후 다른 작업지시를 선택해 주세요.");
                return false;
            }

            setDetailScrollTop(null);
            setSelectedKSK2130_R01(productOrder);
            setKSK2130_R01DetailList([]);
            setKSK2130_R01Filter({ barcodeInput: "" }); // 작업지시 전환 시 스캔 입력값 초기화
            const requestId = ++detailRequestIdRef.current;
            const response = await getKSK2130_R01DetailList(
                productOrder.productOrderNo,
            );
            if (requestId === detailRequestIdRef.current) {
                setKSK2130_R01DetailList(response.data.data ?? []);
                detailGridRef.current?.api.ensureIndexVisible(0, "top");
            }
            return true;
        },
        [
            detailGridRef,
            hasPendingChanges,
            selectedKSK2130_R01?.productOrderNo,
            setDetailScrollTop,
            setKSK2130_R01DetailList,
            setKSK2130_R01Filter,
            setSelectedKSK2130_R01,
        ],
    );

    const fetchData = useCallback(async () => {
        if (hasPendingChanges) {
            showWarning("변경 내용을 저장하거나 초기화한 후 다시 조회해 주세요.");
            return;
        }

        gridRef.current?.api.stopEditing();
        detailGridRef.current?.api.stopEditing();
        detailRequestIdRef.current++;
        setLoadingSpinner(true);
        try {
            setScrollTop(null);
            setDetailScrollTop(null);
            const response = await getKSK2130_R01List(
                "search",
                KSK2130_R01Filter,
            );
            const productOrderList = response.data.data ?? [];
            setKSK2130_R01List(productOrderList);
            setKSK2130_R01DetailList([]);
            setSelectedKSK2130_R01(undefined);

            const firstProductOrder = productOrderList[0];
            if (firstProductOrder) {
                await fetchDetailList(firstProductOrder, false);
                gridRef.current?.api.ensureIndexVisible(0, "top");
                ShowSuccessFetch(
                    setMessage,
                    `조회가 완료되었습니다. (총 ${productOrderList.length}건)`,
                );
                setTimeout(() => setMessage(""), 5000);
            } else {
                setMessage("");
                showWarning("조회된 데이터가 없습니다.");
            }
        } catch {
            showError("작업지시 조회 중 오류가 발생했습니다.");
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        KSK2130_R01Filter,
        detailGridRef,
        fetchDetailList,
        gridRef,
        hasPendingChanges,
        setKSK2130_R01DetailList,
        setKSK2130_R01List,
        setLoadingSpinner,
        setMessage,
        setDetailScrollTop,
        setScrollTop,
        setSelectedKSK2130_R01,
    ]);

    const fetchLotList = useCallback(
        async (productOrder: KSK2130_R01HeaderDto) => {
            if (
                selectedKSK2130_R01?.productOrderNo !== productOrder.productOrderNo
            ) {
                const isAccepted = await fetchDetailList(productOrder);
                if (!isAccepted) return;
            }

            const targetWarehouse = getTargetOutWarehouse(productOrder.workplaceCd);
            const response = await getKSK2130_R01LotList(
                productOrder.productOrderNo,
            );
            const currentDetailList =
                useKSK2130_R01Store.getState().KSK2130_R01DetailList;
            const assignedKeySet = new Set(
                currentDetailList.map((item) => `${item.itemCd}|${item.lotNo}`),
            );
            setKSK2130_R01ModalList(
                (response.data.data ?? []).filter(
                    (item) =>
                        (!targetWarehouse || item.warehouseCd === targetWarehouse) &&
                        !assignedKeySet.has(`${item.itemCd}|${item.lotNo}`),
                ),
            );
            setIsMaterialLotModalVisible(true);
        },
        [
            fetchDetailList,
            getTargetOutWarehouse,
            selectedKSK2130_R01?.productOrderNo,
            setIsMaterialLotModalVisible,
            setKSK2130_R01ModalList,
        ],
    );

    const handleCreateClick = useCallback(async () => {
        if (!selectedKSK2130_R01?.productOrderNo) {
            showWarning("작업지시를 먼저 선택해 주세요.");
            return;
        }
        await fetchLotList(selectedKSK2130_R01);
    }, [fetchLotList, selectedKSK2130_R01]);

    /** 자재 LOT 바코드 스캔 처리 — 출고창고로 후보 제한 + FIFO 검증 후 DetailTable에 신규 행 추가 */
    const handleBarcodeScan = useCallback(
        async (rawCode: string) => {
            if (!selectedKSK2130_R01?.productOrderNo) {
                showWarning("작업지시를 먼저 선택해 주세요.");
                return;
            }
            const scannedLotNo = rawCode.trim().toUpperCase();
            if (!scannedLotNo) return;

            const targetWarehouse = getTargetOutWarehouse(
                selectedKSK2130_R01.workplaceCd,
            );

            const response = await getKSK2130_R01LotList(
                selectedKSK2130_R01.productOrderNo,
            );
            const assignedKeySet = new Set(
                KSK2130_R01DetailList.map((item) => `${item.itemCd}|${item.lotNo}`),
            );
            const candidateList = (response.data.data ?? []).filter(
                (item) =>
                    (!targetWarehouse || item.warehouseCd === targetWarehouse) &&
                    !assignedKeySet.has(`${item.itemCd}|${item.lotNo}`),
            );

            const matched = candidateList.find(
                (item) => item.lotNo?.toUpperCase() === scannedLotNo,
            );
            if (!matched) {
                showWarning(
                    `해당 창고(${targetWarehouse || "미지정"})에서 투입 가능한 LOT을 찾을 수 없습니다: ${scannedLotNo}`,
                );
                return;
            }

            // 창고가 이미 출고창고로 고정되었으므로 품목코드 단위로 FIFO 그룹 구성
            const canonicalGroupList = candidateList.filter(
                (item) => item.itemCd === matched.itemCd,
            );
            const matchedIndex = canonicalGroupList.findIndex(
                (item) => item.lotNo === matched.lotNo,
            );
            if (matchedIndex > 0) {
                showWarning(
                    `선행 LOT(${canonicalGroupList[0].lotNo})을 먼저 스캔하세요.`,
                );
                return;
            }

            insertKSK2130_R01DetailList([matched]);
            ShowSuccessFetch(setMessage, `LOT(${matched.lotNo})이 투입 목록에 추가되었습니다.`);
            setTimeout(() => setMessage(""), 3000);
        },
        [
            KSK2130_R01DetailList,
            getTargetOutWarehouse,
            insertKSK2130_R01DetailList,
            selectedKSK2130_R01,
            setMessage,
        ],
    );

    const handleDeleteClick = useCallback(() => {
        if (deletedKSK2130_R01DetailList.length > 0) {
            removeDeletedKSK2130_R01DetailList(
                deletedKSK2130_R01DetailList,
            );
            initSelectedKSK2130_R01DetailList();
            return;
        }

        const selectedList = KSK2130_R01DetailList.filter((item) =>
            selectedKSK2130_R01DetailList.some(
                (valueIndex) => Number(valueIndex) === item.valueIndex,
            ),
        );
        if (selectedList.length === 0) {
            showWarning("삭제할 투입 LOT을 선택해 주세요.");
            return;
        }

        const insertedValueIndexList = selectedList
            .filter((item) => item.inserted)
            .map((item) => item.valueIndex.toString());
        const existingValueIndexList = selectedList
            .filter((item) => !item.inserted)
            .map((item) => item.valueIndex.toString());

        if (existingValueIndexList.length > 0) {
            addDeletedKSK2130_R01DetailList(existingValueIndexList);
        }
        if (insertedValueIndexList.length > 0) {
            removeKSK2130_R01DetailList(insertedValueIndexList);
        }
        initSelectedKSK2130_R01DetailList();
    }, [
        KSK2130_R01DetailList,
        addDeletedKSK2130_R01DetailList,
        deletedKSK2130_R01DetailList,
        initSelectedKSK2130_R01DetailList,
        removeDeletedKSK2130_R01DetailList,
        removeKSK2130_R01DetailList,
        selectedKSK2130_R01DetailList,
    ]);

    const handleSaveClick = useCallback(async () => {
        const productOrderNo = selectedKSK2130_R01?.productOrderNo;
        if (!productOrderNo) {
            showWarning("작업지시를 먼저 선택해 주세요.");
            return;
        }
        if (!hasPendingChanges) {
            showWarning("저장할 변경사항이 없습니다.");
            return;
        }

        setLoadingSpinner(true);
        try {
            const insertedList = KSK2130_R01DetailList
                .filter((item) => item.inserted)
                .map((item) => ({ ...item, productOrderNo }));
            const deletedList = originalKSK2130_R01DetailList
                .filter((item) =>
                    deletedKSK2130_R01DetailList.some(
                        (valueIndex) => Number(valueIndex) === item.valueIndex,
                    ),
                )
                .map((item) => ({ ...item, productOrderNo }));

            if (deletedList.length > 0) {
                await deleteKSK2130_R01(deletedList);
            }
            if (insertedList.length > 0) {
                await postKSK2130_R01(insertedList);
            }

            const response = await getKSK2130_R01DetailList(productOrderNo);
            detailRequestIdRef.current++;
            setKSK2130_R01DetailList(response.data.data ?? []);
            ShowSuccessFetch(setMessage, "자재 LOT 저장이 완료되었습니다.");
            setTimeout(() => setMessage(""), 5000);
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        KSK2130_R01DetailList,
        deletedKSK2130_R01DetailList,
        hasPendingChanges,
        originalKSK2130_R01DetailList,
        selectedKSK2130_R01,
        setKSK2130_R01DetailList,
        setLoadingSpinner,
        setMessage,
    ]);

    const initContainerData = useCallback(() => {
        gridRef.current?.api.stopEditing();
        gridRef.current?.api.deselectAll();
        detailGridRef.current?.api.stopEditing();
        detailGridRef.current?.api.deselectAll();
        detailRequestIdRef.current++;
        initData();
        setMessage("");
    }, [detailGridRef, gridRef, initData, setMessage]);

    return {
        fetchData,
        fetchDetailList,
        fetchLotList,
        handleCreateClick,
        handleDeleteClick,
        handleSaveClick,
        handleBarcodeScan,
        initContainerData,
    };
};
